import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase/firebase';
 
const C = {
    primary:          '#6a37d4',
    primaryLight:     '#ae8dff',
    primaryContainer: '#ede8ff',
    surface:          '#f3f6ff',
    surfaceLow:       '#eaf1ff',
    surfaceHigh:      '#d6e3f9',
    onBackground:     '#252f3c',
    onSurface:        '#252f3c',
    onSurfaceVariant: '#525c6a',
    outlineVariant:   '#a4aebe',
};
 
interface LoginFormProps {
    onSuccess: () => void;
    onSwitchToSignup?: () => void;
    variant?: 'dropdown' | 'page';
}
 
const LoginForm: React.FC<LoginFormProps> = ({
    onSuccess,
    onSwitchToSignup,
    variant = 'dropdown',
}) => {
    const navigate = useNavigate();
    const [email, setEmail]               = useState('');
    const [password, setPassword]         = useState('');
    const [error, setError]               = useState('');
    const [loading, setLoading]           = useState(false);
    const [resetEmailSent, setResetEmailSent] = useState(false);
 
    const isPage = variant === 'page';
 
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
            if (userDoc.exists()) {
                navigate(userDoc.data().role === 'teacher' ? '/teacher' : '/dashboard');
            } else {
                navigate('/dashboard');
            }
            onSuccess();
        } catch (err: any) {
            setError(err.message || 'Failed to sign in');
        } finally {
            setLoading(false);
        }
    };
 
    const handleForgotPassword = async () => {
        if (!email) { setError('Please enter your email address first'); return; }
        try {
            await sendPasswordResetEmail(auth, email);
            setResetEmailSent(true);
            setError('');
        } catch {
            setError('Failed to send reset email. Please check your email address.');
        }
    };
 
    const inputStyle: React.CSSProperties = {
        width: '100%',
        padding: '11px 14px',
        border: `1.5px solid ${C.outlineVariant}`,
        borderRadius: '10px',
        fontSize: '0.9rem',
        boxSizing: 'border-box',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        background: C.surface,
        color: C.onSurface,
        outline: 'none',
        transition: 'border-color 0.15s',
    };
 
    const labelStyle: React.CSSProperties = {
        display: 'block',
        marginBottom: '5px',
        fontSize: '0.8rem',
        fontWeight: '700',
        color: C.onSurfaceVariant,
        fontFamily: "'Epilogue', sans-serif",
    };
 
    return (
        <div style={{
            padding: isPage ? '48px 40px' : '24px',
            width: isPage ? '100%' : '300px',
            maxWidth: isPage ? '480px' : undefined,
            margin: isPage ? '0 auto' : undefined,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}>
            {/* Header */}
            <h3 style={{
                fontFamily: "'Epilogue', sans-serif",
                fontSize: isPage ? '2rem' : '1.3rem',
                fontWeight: '900',
                color: C.onBackground,
                margin: '0 0 6px 0',
                textAlign: 'center',
            }}>
                {isPage ? 'Welcome Back' : 'Sign In'}
            </h3>
 
            {isPage && (
                <p style={{
                    textAlign: 'center',
                    fontSize: '0.95rem',
                    color: C.onSurfaceVariant,
                    margin: '0 0 32px 0',
                }}>
                    Sign in to continue your story
                </p>
            )}
 
            {/* Error */}
            {error && (
                <div style={{
                    background: '#fef2f2', border: '1px solid #fecaca',
                    borderRadius: '10px', padding: '11px',
                    marginBottom: '16px', color: '#dc2626',
                    fontSize: '0.85rem', textAlign: 'center', fontWeight: '500',
                }}>
                    {error}
                </div>
            )}
 
            {/* Reset email confirmation */}
            {resetEmailSent && (
                <div style={{
                    background: C.primaryContainer,
                    border: `1.5px solid ${C.primary}`,
                    borderRadius: '10px', padding: '11px',
                    marginBottom: '16px', color: C.primary,
                    fontSize: '0.85rem', textAlign: 'center', fontWeight: '600',
                }}>
                    📧 Reset email sent! Check your inbox (and spam folder).
                </div>
            )}
 
            <form onSubmit={handleSubmit}>
                {/* Email */}
                <div style={{ marginBottom: '14px' }}>
                    <label style={labelStyle}>Email</label>
                    <input
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        required
                        style={inputStyle}
                        onFocus={e  => { e.target.style.borderColor = C.primary; }}
                        onBlur={e   => { e.target.style.borderColor = C.outlineVariant; }}
                    />
                </div>
 
                {/* Password */}
                <div style={{ marginBottom: '8px' }}>
                    <label style={labelStyle}>Password</label>
                    <input
                        type="password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        required
                        style={inputStyle}
                        onFocus={e  => { e.target.style.borderColor = C.primary; }}
                        onBlur={e   => { e.target.style.borderColor = C.outlineVariant; }}
                    />
                </div>
 
                {/* Forgot password */}
                <div style={{ textAlign: 'right', marginBottom: '20px' }}>
                    <button
                        type="button"
                        onClick={handleForgotPassword}
                        style={{
                            background: 'none', border: 'none',
                            color: C.primary, fontSize: '0.82rem',
                            fontWeight: '600', cursor: 'pointer',
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            padding: 0, transition: 'opacity 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '0.7'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                    >
                        Forgot password?
                    </button>
                </div>
 
                {/* Submit */}
                <button
                    type="submit"
                    disabled={loading}
                    style={{
                        width: '100%', padding: '12px',
                        background: loading
                            ? C.outlineVariant
                            : `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`,
                        color: '#fff', border: 'none',
                        borderRadius: '9999px',
                        fontSize: '0.95rem', fontWeight: '700',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        marginBottom: '16px',
                        fontFamily: "'Epilogue', sans-serif",
                        boxShadow: loading ? 'none' : `0 4px 14px ${C.primary}40`,
                        transition: 'opacity 0.15s, transform 0.15s',
                    }}
                    onMouseEnter={e => { if (!loading) { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                    {loading ? 'Signing in…' : 'Sign In'}
                </button>
            </form>
 
            {/* Switch to signup */}
            <div style={{ textAlign: 'center', fontSize: '0.85rem', color: C.onSurfaceVariant }}>
                Don't have an account?{' '}
                <button
                    onClick={onSwitchToSignup || (() => navigate('/signup'))}
                    style={{
                        background: 'none', border: 'none',
                        color: C.primary, fontWeight: '700',
                        cursor: 'pointer', fontSize: '0.85rem',
                        fontFamily: "'Epilogue', sans-serif",
                        padding: 0, transition: 'opacity 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '0.7'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                >
                    Create Account
                </button>
            </div>
        </div>
    );
};
 
export default LoginForm;
